import React, { useState, useEffect } from 'react';
import { Timesheet } from '../../../types';
import { Formik, FieldArray } from 'formik';
import { Form, SubmitButton, Input, DatePicker, Select } from 'formik-antd';
import { Row, Col, Button, Typography, Tabs, Collapse } from 'antd';
import moment from 'moment';
import PageHeader from '../../../Components/PageHeader';
import ContentWrapper from '../../../Components/ContentWrapper';
import { TimesheetEntryInput, DayType, TimesheetStatus, UpdateTimesheetInput } from '../../../API';
import withAppState, { IAppStateComponentProps } from '../../../state/withAppState';
import { useFeatureFlag, useFeatureFlagNoContext } from '../../../Components/FeatureFlagGateHook';
import ActivityList from '../../ContractorActivity/ActivityListReview';
import { fetchContractById, isShiftsWorkType } from '../../../utils/contractUtils';
import { LeaveDetailsPanel, LeaveSummary } from 'basealpha-ui';

const validate = (values: Timesheet): any => {
  const errors = {} as any;
  // Trying to submit, check all entries
  values?.entries?.forEach((entry: TimesheetEntryInput | null, index: number): void => {
    if (!entry) {
      return;
    }
    if (entry.leaveId) {
      return;
    }
    if (entry.type === DayType.LEAVE && !entry.leaveType) {
      // User made this day a leave but didn't specify the type
      if (!errors.entries) {
        errors.entries = [];
      }
      errors.entries[index] = {
        leaveType: 'Please specify leave type',
      };
    }
  });
  return errors;
};

const TimesheetForm = ({
  record,
  save,
  close,
  breadcrumbs,
  user,
  leaveSummary,
}: IAppStateComponentProps & {
  record: Timesheet | null;
  save: (data: Timesheet) => void;
  close: () => void;
  breadcrumbs?: any;
  user: string;
  leaveSummary: LeaveSummary | null;
}): React.ReactElement => {
  const readOnly = true;
  const isMonthlyActivityEnabled = useFeatureFlagNoContext('monthly_activity_report', { customerId: parseInt(record?.customerId as string) });
  const [isShifts, setIsShifts] = useState(false);

  useEffect(() => {
    const fetchContract = async () => {
      if (record?.contractId) {
        const contractData = await fetchContractById(record.contractId);
        setIsShifts(isShiftsWorkType(contractData));
      }
    };
    fetchContract();
  }, [record?.contractId]);
  return (
    <div data-testid="timesheet-form">
      <PageHeader
        title={record?.description || ''}
        routes={[
          ...(breadcrumbs || [{ path: '/timesheets', breadcrumbName: 'Timesheets' }]),
          { path: ':id', breadcrumbName: record?.description },
        ]}
      />
      <ContentWrapper>
        <Formik initialValues={record || ({} as Timesheet)} onSubmit={save} validate={validate}>
          {(formProps: any): React.ReactElement => (
            <Form
              layout="horizontal"
              labelCol={{
                span: 6,
              }}
              wrapperCol={{
                span: 18,
              }}
              labelAlign="left"
            >
              <Typography.Title>{formProps.values.employeeName}</Typography.Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name={`fromDate`} label="From">
                    <DatePicker name="fromDate" disabled />
                  </Form.Item>
                  <Form.Item name={`status`} label="Status">
                    <Input name="status" disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name={`toDate`} label="To">
                    <DatePicker name="toDate" disabled />
                  </Form.Item>
                </Col>
              </Row>
              <br />
              {leaveSummary && (
                <Collapse style={{ marginBottom: 16 }}>
                  <Collapse.Panel header="Leave Details" key="leave-details">
                    <LeaveDetailsPanel leaveSummary={leaveSummary} mode="timesheet" />
                  </Collapse.Panel>
                </Collapse>
              )}
              <Tabs defaultActiveKey="timesheet">
                <Tabs.TabPane tab="Timesheet" key="timesheet">
                  <Row
                    gutter={16}
                    style={{
                      background: '#EFEFEF',
                      padding: '10px 0',
                    }}
                  >
                    <Col span={8}>Date</Col>
                    <Col span={8}>Activity</Col>
                    <Col span={8}>Leave Type</Col>
                  </Row>
                  <FieldArray
                    name="entries"
                    render={(arrayHelpers: any): React.ReactElement => (
                      <>
                        {formProps.values.entries &&
                          formProps.values.entries.map((entry: TimesheetEntryInput, index: number) => (
                            <Row
                              key={entry.date}
                              gutter={16}
                              style={{ padding: '5px 0', borderBottom: '1px solid #efefef' }}
                            >
                              <Col span={8}>
                                <Form.Item name={`entries[${index}].date`} label="">
                                  {moment(formProps.values.entries[index].date).format('ddd D')}
                                  {/* <DatePicker name={`entries[${index}].date`} disabled /> */}
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name={`entries[${index}].type`} label="">
                                  <Select
                                    name={`entries[${index}].type`}
                                    disabled={
                                      !!formProps.values.entries[index].leaveId ||
                                      formProps.values.entries[index].type === DayType.HOLIDAY ||
                                      readOnly
                                    }
                                  >
                                    {isShifts && (
                                      <Select.Option value={DayType.OFF_SHIFT}>Off Shift</Select.Option>
                                    )}
                                    <Select.Option value={DayType.WORKED}>Worked</Select.Option>
                                    <Select.Option value={DayType.LEAVE}>Leave</Select.Option>
                                    <Select.Option value={DayType.CUSTOM_HOLIDAY}>Public Holiday</Select.Option>
                                    {formProps.values.entries[index].type === DayType.HOLIDAY && (
                                      <Select.Option value={DayType.HOLIDAY}>Holiday</Select.Option>
                                    )}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                {formProps.values.entries[index].type === DayType.LEAVE && (
                                  <Form.Item name={`entries[${index}].leaveType`} label="">
                                    <Select
                                      name={`entries[${index}].leaveType`}
                                      disabled={!!formProps.values.entries[index].leaveId || readOnly}
                                    >
                                      <Select.Option value={5}>Annual Leave</Select.Option>
                                      <Select.Option value={6}>Sick Leave</Select.Option>
                                      <Select.Option value={4}>Unpaid Leave</Select.Option>
                                      <Select.Option value={8}>Maternity Leave</Select.Option>
                                      <Select.Option value={11}>Maternity Leave - Half Pay</Select.Option>
                                      <Select.Option value={12}>Maternity Leave - Full Pay</Select.Option>
                                      <Select.Option value={9}>Paternity Leave</Select.Option>
                                      <Select.Option value={10}>Compassionate Leave</Select.Option>
                                    </Select>
                                  </Form.Item>
                                )}
                              </Col>
                            </Row>
                          ))}
                      </>
                    )}
                  />
                </Tabs.TabPane>
                {isMonthlyActivityEnabled && (
                  <Tabs.TabPane tab="Monthly Activity Report" key="monthlyActivityReport">
                    {record?.id && <ActivityList timesheetId={record?.id} timesheet={record} />}
                  </Tabs.TabPane>
                )}
              </Tabs>
              <Row style={{ marginTop: '20px' }}>
                <Col span={12}>
                  <h3>Status:</h3>
                  <p>
                    <b>Date Submitted: </b> {moment(formProps.values.submittedDate).format('MMMM Do YYYY')}
                  </p>
                  {formProps.values.approver1 && (
                    <p>
                      <b>{formProps.values.approver1Name}: </b>
                      {formProps.values.approved1
                        ? `Approved (${moment(formProps.values.approved1Date).format('MMMM Do YYYY')})`
                        : 'Pending Approval'}
                    </p>
                  )}
                  {formProps.values.approver2 && (
                    <p>
                      <b>{formProps.values.approver2Name}: </b>
                      {formProps.values.approved2
                        ? `Approved (${moment(formProps.values.approved2Date).format('MMMM Do YYYY')})`
                        : 'Pending Approval'}
                    </p>
                  )}
                  {(formProps.values.statusChanges || []).length > 0 && <h3>Timeline:</h3>}
                  <ul>
                    {(formProps.values.statusChanges || []).map((change: any) => (
                      <li>{`${moment(change.date).format('MMMM Do YYYY')}: ${change.message}`}</li>
                    ))}
                  </ul>
                </Col>
                <Col span={12}>
                  <div style={{ marginTop: '20px', textAlign: 'right' }}>
                    {((formProps.values.approver1 === user && !formProps.values.approved1) ||
                      (formProps.values.approver2 === user && !formProps.values.approved2)) && (
                      <>
                        <Form.Item name={`approverName`} label="Your Name">
                          <Input name="approverName" />
                        </Form.Item>
                        <Button
                          type="primary"
                          onClick={async (): Promise<void> => {
                            await formProps.setFieldValue('approving', true);
                            formProps.submitForm();
                          }}
                          disabled={formProps.isSubmitting}
                          loading={formProps.isSubmitting}
                          style={{ marginRight: '10px' }}
                        >
                          Approve Timesheet
                        </Button>
                      </>
                    )}
                    {((formProps.values.approver1 === user &&
                      (formProps.values.approved1 === null || formProps.values.status === TimesheetStatus.SUBMITTED)) ||
                      (formProps.values.approver2 === user &&
                        (formProps.values.approved2 === null ||
                          formProps.values.status === TimesheetStatus.SUBMITTED))) && (
                      <>
                        <Button
                          type="primary" danger
                          onClick={async (): Promise<void> => {
                            await formProps.setFieldValue('rejecting', true);
                            await formProps.setFieldValue('status', TimesheetStatus.REJECTED);
                            await formProps.setFieldValue(
                              formProps.values.approver1 === user ? 'approved1' : 'approved2',
                              false,
                            );
                            formProps.submitForm();
                          }}
                          disabled={formProps.isSubmitting}
                          loading={formProps.isSubmitting}
                          style={{ marginRight: '10px' }}
                        >
                          Reject Timesheet
                        </Button>
                      </>
                    )}
                    <Button type="default" onClick={close}>
                      Back
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </ContentWrapper>
    </div>
  );
};

export default withAppState(TimesheetForm);
